import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Hook options for fetching researcher signatures
 */
interface UseResearcherSignatureOptions {
  /** UUID of the investigation */
  uuidInvestigation: string;
  /** UUID of the researcher whose signature to fetch */
  uuidResearcher: string;
  /** Whether the hook should fetch data (default: true) */
  enabled?: boolean;
}

/**
 * Return type of useResearcherSignature hook
 */
interface UseResearcherSignatureReturn {
  /** Signed URL from API (null if not loaded or error) */
  signatureUrl: string | null;
  /** Loading state */
  loading: boolean;
  /** Error message (null if no error) */
  error: string | null;
}

/**
 * Custom hook to fetch researcher signature URL from API
 * 
 * Simple hook that fetches a signed URL for a researcher's signature image.
 * 
 * @example
 * ```tsx
 * const { signatureUrl, loading, error } = useResearcherSignature({
 *   uuidInvestigation: 'investigation-123',
 *   uuidResearcher: 'researcher-456',
 * });
 * 
 * if (signatureUrl) {
 *   return <Image src={signatureUrl} />;
 * }
 * ```
 */
export const useResearcherSignature = ({
  uuidInvestigation,
  uuidResearcher,
  enabled = true,
}: UseResearcherSignatureOptions): UseResearcherSignatureReturn => {
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Don't fetch if disabled or missing required params
    if (!enabled || !uuidInvestigation || !uuidResearcher) {
      return;
    }

    setLoading(true);
    setError(null);

    const url = `${import.meta.env.VITE_APP_API_URL}/researcher/investigation/${uuidInvestigation}/signature/${uuidResearcher}`;

    axios.get(url, {
      headers: { 
        Authorization: localStorage.getItem('jwt') 
      }
    })
    .then(response => {
      // Check if backend returned base64 directly (preferred - no CORS issues!)
      if (response.data?.signatureBase64) {
        setSignatureUrl(response.data.signatureBase64);
      }
      // Fallback to URL if backend returns presigned URL (requires CORS)
      else if (response.data?.signatureUrl) {
        setSignatureUrl(response.data.signatureUrl);
      } else {
        setError('Signature data not found in response');
      }
    })
    .catch(err => {
      setError(err.response?.data?.message || 'Failed to fetch signature');
      setSignatureUrl(null);
    })
    .finally(() => {
      setLoading(false);
    });
    
  }, [uuidInvestigation, uuidResearcher, enabled]);

  return {
    signatureUrl,
    loading,
    error,
  };
};
